import { Static, Type } from '@sinclair/typebox';
import { AdminCrmSubjectSchema } from './crm-contact';
import { NonConversionReason } from './non-conversion';

// =====================================================================
// CRM — formation (lot 3). Enum dual-defined avec schema.prisma.
//
// L'inscription se fait à la main dans le compte Propelsia qui porte les formations (séances de
// groupe). L'admin lit ces séances, l'équipe y coche la présence ; les suites (SMS) en découlent.
// =====================================================================

export enum CrmTrainingPresence {
  PRESENT = 'PRESENT',
  ABSENT = 'ABSENT',
}

/**
 * Nature d'une séance de groupe du compte Propelsia : formation (prise en main, suites SMS) ou
 * démo (séance de présentation ; aucun SMS de suite, seulement l'email de suite s'il est réglé).
 * La prestation du rendez-vous dit sa nature (`CRM_TRAINING_PRESTATION_IDS` / `CRM_DEMO_PRESTATION_IDS`).
 */
export enum CrmSessionKind {
  TRAINING = 'TRAINING',
  DEMO = 'DEMO',
}

/**
 * Nature d'un email de suite de séance : après une démo suivie, après une formation suivie. Depuis
 * le 22/10/2026 un email est rédigé PAR PRESTATION de séance (chaque formation, chaque démo) dans
 * « Campagnes » ; la nature en découle. Extensible à `DEMO_MISSED` / `TRAINING_MISSED` sans rien
 * changer au moteur. Dual-defined avec schema.prisma.
 */
export enum CrmSessionFollowupKind {
  DEMO_ATTENDED = 'DEMO_ATTENDED',
  TRAINING_ATTENDED = 'TRAINING_ATTENDED',
}

/** Nature de séance visée par chaque emplacement. */
export const CRM_SESSION_FOLLOWUP_SESSION_KIND: Record<CrmSessionFollowupKind, CrmSessionKind> = {
  [CrmSessionFollowupKind.DEMO_ATTENDED]: CrmSessionKind.DEMO,
  [CrmSessionFollowupKind.TRAINING_ATTENDED]: CrmSessionKind.TRAINING,
};

/** Email de suite « séance suivie » d'une nature de séance. */
export const CRM_SESSION_KIND_FOLLOWUP: Record<CrmSessionKind, CrmSessionFollowupKind> = {
  [CrmSessionKind.DEMO]: CrmSessionFollowupKind.DEMO_ATTENDED,
  [CrmSessionKind.TRAINING]: CrmSessionFollowupKind.TRAINING_ATTENDED,
};

/** Après une absence sans réinscription, le dernier SMS part sept jours plus tard. */
export const CRM_TRAINING_LAST_CALL_DAYS = 7;
/** Après une séance suivie, le SMS de reprise part deux jours plus tard. */
export const CRM_TRAINING_ATTENDED_FOLLOWUP_DAYS = 2;
/** Deux absences de suite : on arrête de proposer la formation. */
export const CRM_TRAINING_MAX_ABSENCES = 2;
/** Une séance passée depuis plus longtemps sans présence saisie remonte « en retard ». */
export const CRM_TRAINING_MARK_LATE_HOURS = 24;

export const AdminCrmTrainingAttendeeSchema = Type.Object({
  appointment_id: Type.Number(),
  name: Type.String(),
  phone_e164: Type.Optional(Type.String()),
  email: Type.Optional(Type.String()),
  /** Fiche CRM retrouvée par le numéro ou l'email du participant. */
  subject: Type.Optional(AdminCrmSubjectSchema),
  presence: Type.Optional(Type.Enum(CrmTrainingPresence)),
  marked_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'marked_at', with_time: true } }),
  ),
  marked_by_name: Type.Optional(Type.String()),
  /** Absences de la personne, toutes séances confondues. */
  absences_count: Type.Number(),
  /** Deux absences : on ne propose plus la formation. */
  training_stopped: Type.Boolean(),
  /** Raison de l'absence, donnée par la personne sur la page à une question (dernière relance). */
  missed_reason: Type.Optional(Type.Enum(NonConversionReason)),
  missed_note: Type.Optional(Type.String()),
});
export type AdminCrmTrainingAttendee = Static<typeof AdminCrmTrainingAttendeeSchema>;

export const AdminCrmTrainingSessionSchema = Type.Object({
  /** `<prestation_id>:<début ISO>` */
  key: Type.String(),
  kind: Type.Enum(CrmSessionKind),
  prestation_name: Type.String(),
  start_formatted: Type.String({ format: 'date-time', meta: { source_key: 'start', with_time: true } }),
  is_past: Type.Boolean(),
  /** Séance passée depuis plus de 24 h avec des présences non saisies. */
  is_late: Type.Boolean(),
  attendees: Type.Array(AdminCrmTrainingAttendeeSchema),
  to_mark: Type.Number(),
});
export type AdminCrmTrainingSession = Static<typeof AdminCrmTrainingSessionSchema>;

export const AdminCrmTrainingSessionsResponseSchema = Type.Object({
  /** `false` : aucun compte de formation configuré (`CRM_TRAINING_PROFESSIONAL_ID`). */
  configured: Type.Boolean(),
  sessions: Type.Array(AdminCrmTrainingSessionSchema),
});
export type AdminCrmTrainingSessionsResponse = Static<typeof AdminCrmTrainingSessionsResponseSchema>;

export enum AdminCrmTrainingView {
  UPCOMING = 'upcoming',
  PAST = 'past',
}

export const AdminCrmTrainingMarkBodySchema = Type.Object({
  appointment_id: Type.Number(),
  presence: Type.Enum(CrmTrainingPresence),
});
export type AdminCrmTrainingMarkBody = Static<typeof AdminCrmTrainingMarkBodySchema>;

/** Formation d'une personne, telle qu'affichée sur sa fiche. */
export const AdminCrmTrainingSummarySchema = Type.Object({
  sessions: Type.Array(
    Type.Object({
      appointment_id: Type.Number(),
      kind: Type.Enum(CrmSessionKind),
      prestation_name: Type.String(),
      start_formatted: Type.String({ format: 'date-time', meta: { source_key: 'start', with_time: true } }),
      presence: Type.Optional(Type.Enum(CrmTrainingPresence)),
      enrolled_at_formatted: Type.Optional(Type.String({ format: 'date-time', meta: { source_key: 'enrolled_at' } })),
    }),
  ),
  /** Absences aux formations seulement (une démo manquée ne compte pas). */
  absences_count: Type.Number(),
  training_stopped: Type.Boolean(),
});
export type AdminCrmTrainingSummary = Static<typeof AdminCrmTrainingSummarySchema>;
